import React, { useEffect, useMemo, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import {
  ConcretingQueriesGetAllWithSortedPaginationArgs, EParameter, ETravelStatus,
  EWhereClausesOperators, ParamQueriesGetAllWithPaginationArgs, TravelType
} from "@graphql/helpers/types.graphql";
import {
  concretingAllWithSortedPagination
} from "@graphql/groups/concreting/query.graphql";
import { TravelDialog } from "src/molecules";
import { useSearchParams } from "react-router-dom";
import * as Styled from "@molecules/BaseCardItem/styles";
import { ReactSVG } from "react-svg";
import { InvoiceInstallmentStatus,
  TravelStatusCanceled,
  TravelStatusDelivered,
  TravelStatusDischarging,
  TravelStatusInConstruction,
  TravelStatusInTransit,
  TravelStatusPreparing,
  TravelStatusProgrammed
} from "@assets/icons";
import { BaseCardMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";
import utils from "@helpers/utils";
import { intervalToDuration } from "date-fns";
import { useAuth } from "@hooks/useAuth";
import ExpiredSession from "src/atoms/ExpiredSession";
import { paramAllWithPagination, TParamAllWithPaginationQueries } from "@graphql/groups/param/query.graphql";
import { TravelSharingContainer, LoginFormContainer } from "./styles";

const TravelSharing: React.FC = () => {

  const { updateUser } = useAuth();
  const [travelDialogOpen, setTravelDialogOpen] = React.useState(false);
  const [travel, setTravel] = React.useState<TravelType | undefined>(undefined);
  const [googleKey, setGoogleKey] = React.useState(false);
  const [requestToken, setRequestToken] = React.useState(false);
  const { t } = useTranslation();
  const [nowDate, setNowDate] = useState(new Date());
  const [searchParams, setSearchParams] = useSearchParams();

  const token = searchParams.get("token");
  const concretingId = searchParams.get("concretingId");
  const travelId = searchParams.get("travelId");

  const baseCardIcons: IBaseCardIcons = useMemo(() => ({
    travel: {
      PROGRAMMED: {
        icon: TravelStatusProgrammed,
        color: "#64DD17",
        labelStatus: t(BaseCardMessages.statusProgrammedLabelText),
        prefix: t(BaseCardMessages.statusProgrammedPrefixText)
      },
      PREPARING: {
        icon: TravelStatusPreparing,
        color: "#ED7117",
        labelStatus: t(BaseCardMessages.statusPreparingLabelText)
      },
      IN_TRANSIT: {
        icon: TravelStatusInTransit,
        color: "#7E73F8",
        labelStatus: t(BaseCardMessages.statusInTransitLabelText),
        prefix: t(BaseCardMessages.statusInTransitPrefixText)
      },
      IN_CONSTRUCTION: {
        icon: TravelStatusInConstruction,
        color: "#C7B301",
        labelStatus: t(BaseCardMessages.statusInConstructionLabelText),
        prefix: t(BaseCardMessages.statusInConstructionPrefixText),
        labelData: `${t(
          BaseCardMessages.statusInConstructionDescriptionText
        )}:`
      },
      DISCHARGING: {
        icon: TravelStatusDischarging,
        color: "#616161",
        labelStatus: t(BaseCardMessages.statusDischargingLabelText),
        prefix: t(BaseCardMessages.statusDischargingPrefixText),
        labelData: `${t(BaseCardMessages.statusDischargingDescriptionText)}:`
      },
      DELIVERED: {
        icon: TravelStatusDelivered,
        color: "#64DD17",
        labelStatus: t(BaseCardMessages.statusDeliveredLabelText),
        labelData: `${t(BaseCardMessages.statusDeliveredDescriptionText)}:`
      },
      CANCELED: {
        icon: TravelStatusCanceled,
        color: "#FF0000",
        labelStatus: t(BaseCardMessages.statusCanceledLabelText)
      },
      INVOICE_ISSUED: {
        icon: InvoiceInstallmentStatus,
        color: "#64DD17",
        labelStatus: t(BaseCardMessages.statusInvoiceIssuedLabelText),
        prefix: t(BaseCardMessages.statusInvoiceIssuedPrefixText)
      }
    }
  } as IBaseCardIcons), [t]);

  interface IIcons {
    [key: string]: {
      icon: string;
      color: string;
      labelStatus?: string;
      prefix?: string;
      labelData?: string;
    };
  }
  interface IBaseCardIcons {
    travel: IIcons;
  }
  useEffect(() => {
    localStorage.setItem("@Topcon:token", String(token));
    updateUser();
    setRequestToken(true);
  }, [token]);

  const [getParams, {
    data: googleKeyApi
  }] = useLazyQuery<
    TParamAllWithPaginationQueries,
    ParamQueriesGetAllWithPaginationArgs
  >(paramAllWithPagination(["id", "name", "key"]), {
    fetchPolicy: "network-only"
  });

  const [getTravelById, {
    loading: loadingConcretings,
    error: errorConcretings,
    data: dataConcretings
  }] = useLazyQuery(
    concretingAllWithSortedPagination([
      "id",
      "status",
      "scheduledDate",
      "programmedVolume",
      "deliveredVolume",
      "pendency",
      "contractConcreteRecipe.concreteRecipe.description",
      "contract.construction.name",
      "contract.construction.client.pendency",
      "contract.pendency",
      "travels.id",
      "travels.fleetExternalId",
      "travels.status",
      "travels.loadLabel",
      "travels.documentNumber",
      "travels.volume",
      "travels.scheduledTime",
      "travels.invoiceIssueTime",
      "travels.plantDepartureTime",
      "travels.dischargeStartTime",
      "travels.dischargeEndTime",
      "travels.finishTime",
      "travels.constructionStartTime",
      "travels.constructionEndTime",
      "travels.concreteMixer.description",
      "travels.concreteMixer.code",
      "travels.concreteMixer.licensePlate",
      "travels.driver.name"
    ])
  );

  useEffect(() => {
    if (googleKeyApi?.param.getAllWithPagination.status === "SUCCESS") {

      const result = googleKeyApi?.param.getAllWithPagination.result;

      if (result && result?.length > 0) {
        localStorage.setItem("@Topcon:google", result[0]?.key ?? "");
        setGoogleKey(true);
      }
    }
  }, [googleKeyApi]);

  useEffect(() => {
    if (requestToken) {
      getParams({
        variables: {
          where: [
            {
              property: "name",
              operator: EWhereClausesOperators.EQUALS,
              value: EParameter.GOOGLE
            }
          ]
        }
      });
    }
  }, [getParams, requestToken]);

  // search by id from client
  useEffect(() => {
    const filters = [
      {
        property: "id",
        operator: EWhereClausesOperators.EQUALS,
        value: concretingId
      }
    ];
    const variables: ConcretingQueriesGetAllWithSortedPaginationArgs = {
      where: filters
    };

    if (requestToken) {
      getTravelById({ variables });
    }
  }, [getTravelById, requestToken]);
  // from client id, research for travel//
  useEffect(() => {
    if (dataConcretings) {
      const [concreting] = dataConcretings.concreting.getAllWithSortedPagination.result;
      const [travelByID] = concreting.travels.filter((travel) => travel.id === travelId);

      setTravelDialogOpen(true);
      setTravel(travelByID);
    }
  }, [dataConcretings]);

  const contentTravel = useMemo(() => {
    if (!travel) return null;

    travel.status;

    const showCompleteData = travel.status === "IN_TRANSIT"
      || travel.status === "IN_CONSTRUCTION"
      || travel.status === "DISCHARGING"
      || travel.status === "DELIVERED"
      || travel.status === "INVOICE_ISSUED";

    const programmedTime = travel.scheduledTime
      && utils.formatDateIfHave(travel.scheduledTime, "timeWithoutSeconds");
    const showProgrammedTime = travel.status === "PREPARING" || travel.status === "CANCELED";
    const showTimeStatus = travel.status !== "CANCELED" && travel.status !== "PREPARING";

    let timeStatus;
    let timeStartStatus;

    switch (travel.status) {
      case ETravelStatus.INVOICE_ISSUED:
        if (travel.invoiceIssueTime) {
          timeStatus = utils.formatDateIfHave(
            new Date(travel.invoiceIssueTime),
            "timeWithoutSeconds"
          );
        }
        break;
      case ETravelStatus.IN_TRANSIT:
        if (travel.plantDepartureTime) {
          timeStatus = utils.formatDateIfHave(
            intervalToDuration({
              start: new Date(travel.plantDepartureTime),
              end: nowDate
            }),
            "durationDescriptiveTime"
          );
        }
        break;
      case ETravelStatus.IN_CONSTRUCTION:
        if (travel.constructionStartTime) {
          timeStatus = utils.formatDateIfHave(
            intervalToDuration({
              start: new Date(travel.constructionStartTime),
              end: nowDate
            }),
            "durationDescriptiveTime"
          );
          timeStartStatus = utils.formatDateIfHave(
            new Date(travel.constructionStartTime),
            "timeWithoutSeconds"
          );
        }
        break;
      case ETravelStatus.DISCHARGING:
        if (travel.dischargeStartTime) {
          timeStatus = utils.formatDateIfHave(
            intervalToDuration({
              start: new Date(travel.dischargeStartTime),
              end: nowDate
            }),
            "durationDescriptiveTime"
          );
          timeStartStatus = utils.formatDateIfHave(
            new Date(travel.dischargeStartTime),
            "timeWithoutSeconds"
          );
        }

        break;
      case ETravelStatus.DELIVERED:
        if (travel.plantDepartureTime) {
          const startTime = new Date(
            travel.plantDepartureTime ?? travel.invoiceIssueTime
          );
          let endTime = new Date(
            travel.invoiceIssueTime ?? travel.plantDepartureTime
          );

          if (travel.dischargeEndTime) {
            endTime = new Date(travel.dischargeEndTime);
          } else if (travel.constructionEndTime) {
            endTime = new Date(travel.constructionEndTime);
          } else if (travel.finishTime) {
            endTime = new Date(travel.finishTime);
          }

          timeStatus = utils.formatDateIfHave(
            intervalToDuration({
              start: startTime,
              end: endTime
            }),
            "durationDescriptiveTime"
          );
          timeStartStatus = utils.formatDateIfHave(
            endTime,
            "timeWithoutSeconds"
          );
        }
        break;
      case ETravelStatus.PROGRAMMED:
        if (travel.scheduledTime) {
          timeStatus = utils.formatDateIfHave(
            intervalToDuration({
              start: nowDate,
              end: new Date(travel.scheduledTime)
            }),
            "durationDescriptiveTime"
          );
        }
        break;
      default:
        break;
    }

    let prefix = "";
    let suffix = "";

    if (showTimeStatus) {
      if (travel.status !== "DELIVERED") {
        prefix = baseCardIcons.travel[travel.status]?.prefix ?? "";
      }
      if (
        travel.status === "PROGRAMMED"
        && nowDate.getTime() > new Date(travel.scheduledTime).getTime()
      ) {
        prefix = "para";
        suffix = " atrás";
      }
    }

    return (
      <div>
        {showCompleteData && (
          <div className="inlineContent">
            <div>
              <span className="titleContent">
                {`${t(BaseCardMessages.travelEquipmentText)}`}:{" "}
              </span>
              {travel.concreteMixer?.code ?? travel.concreteMixer?.description}
            </div>
            <div>
              <span className="titleContent">
                {`${t(BaseCardMessages.travelPlateText)}`}:{" "}
              </span>
              {travel.concreteMixer?.licensePlate}
            </div>
          </div>
        )}
        {showCompleteData && travel.driver && (
          <div>
            <span className="titleContent">
              {`${t(BaseCardMessages.travelDriverText)}`}:{" "}
            </span>
            {travel.driver.name}
          </div>
        )}
        <div>
          {showProgrammedTime ? (
            <>
              <span className="titleContent">
                {`${t(BaseCardMessages.travelProgrammedText)}`}:{" "}
              </span>
              {programmedTime}
            </>
          ) : (
            <>
              <span className="titleContent">
                {baseCardIcons.travel[travel.status].labelData}{" "}
              </span>
              {timeStartStatus}
            </>
          )}
        </div>
        {showCompleteData && (
          <div>
            <div className="inlineContent">
              <div>
                <span className="titleContent">
                  {`${t(BaseCardMessages.travelDocumentNumberText)}`}:{" "}
                </span>
                {travel.documentNumber}
              </div>
              <div>
                <span className="titleContent">
                  {`${t(BaseCardMessages.travelLoadLabelText)}`}:{" "}
                </span>
                {travel.loadLabel}
              </div>
            </div>
            <div>
              <span className="titleContent">
                {`${t(BaseCardMessages.volumeText)}: `}
              </span>
              {travel.volume}
            </div>
          </div>
        )}
        <div>
          <span className="contentColor">
            {baseCardIcons.travel[travel.status].labelStatus}
          </span>
          {showTimeStatus && ` ${prefix} ${timeStatus}${suffix}`}
        </div>
      </div>
    );
  }, [travel, t, baseCardIcons.travel, nowDate]);

  const contentCard = useMemo(() => {
    if (!travel) return undefined;

    return (
      <Styled.Content>
        <Styled.ContentContainer>
          <Styled.IconContainer
            color={baseCardIcons.travel[travel?.status ?? ""].color}
          >
            <ReactSVG src={baseCardIcons.travel[travel?.status ?? ""].icon} />
          </Styled.IconContainer>

          <Styled.InfoContainer
            color={baseCardIcons.travel[travel?.status ?? ""].color}
          >
            {contentTravel}
          </Styled.InfoContainer>
        </Styled.ContentContainer>
      </Styled.Content>
    );
  }, [baseCardIcons.travel, travel, contentTravel]);

  return (
    <TravelSharingContainer>
      <LoginFormContainer>
        { travel && googleKey && (
          <TravelDialog
            open={travelDialogOpen}
            travel={travel}
            header={contentCard}
            sharing={false}
            handleClose={() => null}
          />
        )}
      </LoginFormContainer>
    </TravelSharingContainer>
  );
};

export default TravelSharing;
